import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState } from '@/components/common/States';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { notificationsApi } from '@/services/api/endpoints/notifications';
import { queryKeys } from '@/services/api/queryKeys';
import type { AppNotification } from '@/types/notification';
import { formatDateTime } from '@/utils/format';

const PAGE_SIZE = 20;

/**
 * The full notification inbox — the bell's panel shows only the latest few.
 * Everything is the caller's own; the server resolves the recipient.
 */
export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const [page, setPage] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const params = { page, size: PAGE_SIZE, unreadOnly };
  const feed = useQuery({
    queryKey: queryKeys.notifications.feed(params),
    queryFn: () => notificationsApi.feed(params),
    placeholderData: (previous) => previous,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: refresh,
    onError: (error) => handleApiError(error),
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: (result) => {
      toast.success(result.marked === 1 ? '1 notification marked read' : `${result.marked} notifications marked read`);
      refresh();
    },
    onError: (error) => handleApiError(error),
  });

  const open = (notification: AppNotification) => {
    if (notification.readAt === undefined) {
      markRead.mutate(notification.id);
    }
    if (notification.link !== undefined && notification.link !== '') {
      navigate(notification.link);
    }
  };

  const pagination = feed.data?.pagination;
  const items = feed.data?.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[900px]">
      <PageHeader
        title="Notifications"
        description="Everything addressed to you, newest first."
        icon={<Bell className="h-5 w-5" aria-hidden="true" />}
        action={
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-[13px] font-medium text-ink transition-colors hover:bg-surface disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-[var(--sms-accent-ring)]"
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
          </button>
        }
      />

      <div role="group" aria-label="Filter" className="mb-4 inline-flex rounded-md border border-line bg-card p-0.5 text-[13px]">
        {[
          { label: 'All', value: false },
          { label: 'Unread', value: true },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            aria-pressed={unreadOnly === option.value}
            onClick={() => {
              setUnreadOnly(option.value);
              setPage(0);
            }}
            className={`rounded px-3 py-1.5 transition-colors focus-visible:outline-2 focus-visible:outline-[var(--sms-accent-ring)] ${
              unreadOnly === option.value ? 'bg-surface font-semibold text-ink' : 'text-muted hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {feed.isPending ? (
        <ul className="space-y-2" aria-hidden="true">
          {Array.from({ length: 5 }, (_, index) => (
            <li key={index} className="skeleton h-[68px] rounded-lg" />
          ))}
        </ul>
      ) : feed.isError ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title={unreadOnly ? 'Nothing unread' : 'No notifications yet'}
          description={unreadOnly ? 'You are all caught up.' : 'Messages about your work, classes and family will appear here.'}
        />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-card">
          {items.map((notification) => {
            const isUnread = notification.readAt === undefined;
            return (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => open(notification)}
                  className="flex w-full gap-3 px-4 py-3.5 text-left transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--sms-accent-ring)]"
                >
                  <span
                    className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isUnread ? 'bg-accent' : 'bg-transparent'}`}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-[14px] ${isUnread ? 'font-semibold text-ink' : 'text-muted'}`}>
                      {isUnread && <span className="sr-only">Unread: </span>}
                      {notification.title}
                    </span>
                    {notification.body !== undefined && notification.body !== '' && (
                      <span className="mt-0.5 block text-[13px] text-muted">{notification.body}</span>
                    )}
                    <time dateTime={notification.createdAt} className="mt-1 block text-[12px] text-subtle">
                      {formatDateTime(notification.createdAt)}
                    </time>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {pagination !== undefined && pagination.totalPages > 1 && (
        <nav aria-label="Pages" className="mt-4 flex items-center justify-between text-[13px] text-muted">
          <span>
            Page {pagination.page + 1} of {pagination.totalPages}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              disabled={pagination.first}
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              className="rounded-md border border-line bg-card px-3 py-1.5 text-ink hover:bg-surface disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.last}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-md border border-line bg-card px-3 py-1.5 text-ink hover:bg-surface disabled:opacity-50"
            >
              Next
            </button>
          </span>
        </nav>
      )}
    </div>
  );
}
